/**
 * @file Service for the executors
 * @author Heino Lenting
 */
import * as moment from 'moment';
import { TaskDrop } from './executors/task-drop';
import { ServiceDrop } from './executors/service-drop';
import { AbsenceDrop } from './executors/absence-drop';
import { AbsenceMove } from './executors/absence-move';
import { AppointmentMove } from './executors/appointment-move';
import { AssignmentMove } from './executors/assignment-move';
import { AttendantMove } from './executors/attendant-move';
import { AppointmentDelete } from './executors/appointment-delete';
import { AssignmentDelete } from './executors/assignment-delete';
import { AttendantDelete } from './executors/attendant-delete';
import { AbsenceDropEdit } from './executors/absence-drop-edit';
import { ServiceDropEdit } from './executors/service-drop-edit';
import { TaskDropEdit } from './executors/task-drop-edit';
import { AbsenceDelete } from './executors/absence-delete';
import { DomainService } from '../domain.service';
import { OverlayService } from '../core/overlay.service';
import { AbsenceResize } from './executors/absence-resize';
import { AppointmentResize } from './executors/appointment-resize';
import { AttendantResize } from './executors/attendant-resize';
import { AssignmentResize } from './executors/assignment-resize';
import { ShiftAdhoc } from './executors/shift-adhoc';
import { TaskAdhoc } from './executors/task-adhoc';
import { AbsenceAdhoc } from './executors/absence-adhoc';
import { AppointmentDrop } from './executors/appointment-drop';
import { AppointmentDropEdit } from './executors/appointment-drop-edit';
import { DropTargetsService } from './drop-targets.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../core/dialog.service';
import { DropExtra } from './executors/drop-extra';
import { Drop } from './executors/drop';
import { Back } from './executors/back';
import { UserService } from '../user.service';
import { ResourceService } from '../resource.service';
import { TaskDropPrognosed } from './executors/task-drop-prognosed';
import { AttendantDrop } from './executors/attendant-drop';
import { AttendantDropEdit } from './executors/attendant-drop-edit';
/**
 * Service to retrieve and execute the executors
 */
var ExecutorsService = /** @class */ (function () {
    /**
     * Constructs a Executors Service
     */
    function ExecutorsService(domainService, overlayService, dropTargetsService, diaglogService, translateService, userService, resourceService) {
        this.domainService = domainService;
        this.overlayService = overlayService;
        this.dropTargetsService = dropTargetsService;
        this.diaglogService = diaglogService;
        this.translateService = translateService;
        this.userService = userService;
        this.resourceService = resourceService;
        // Events are stored in the service and this number
        // limits the total of concurrent events
        this.maxExecutorEvents = 5;
        // The list of executor events
        this.events = [];
        // List of executors
        this.executors = this.loadExecutors();
    }
    /**
    * Load the executors
    */
    ExecutorsService.prototype.loadExecutors = function () {
        var _this = this;
        var executors = [];
        [
            TaskDrop,
            TaskDropEdit,
            TaskDropPrognosed,
            ServiceDrop,
            ServiceDropEdit,
            AbsenceDrop,
            AbsenceDropEdit,
            AbsenceMove,
            AbsenceDelete,
            AppointmentDrop,
            AppointmentDropEdit,
            AppointmentMove,
            AssignmentMove,
            AttendantMove,
            AppointmentDelete,
            AssignmentDelete,
            AttendantDelete,
            AbsenceResize,
            AppointmentResize,
            AttendantResize,
            AssignmentResize,
            AttendantDrop,
            AttendantDropEdit,
            ShiftAdhoc,
            TaskAdhoc,
            AbsenceAdhoc,
            Drop,
            DropExtra,
            Back
        ].forEach(function (Executor) {
            executors.push(new Executor(_this.domainService, _this.overlayService, _this.dropTargetsService, _this.diaglogService, _this.translateService, _this.userService, _this.resourceService));
        });
        return executors;
    };
    /**
     * Method used for initiating an event
     * @param {ExecutorName} name The name of the executor
     * @param {ExecutorData} data The executor data
     * @returns {Promise<any>}
     */
    ExecutorsService.prototype.initiate = function (name, data) {
        // Try to find the executor, else reject it
        return this.getExecutorByName(name).then(function (executor) {
            // Let execute the executor!
            return executor.execute(data);
        });
    };
    /**
     * Get all executors for given scope
     * @param {ExecutorAction} action The action of the executors to look for
     * @param {ExecutorScope} scope Filter of the executors based on the scope
     * @returns {ExecutorBase[]}
     */
    ExecutorsService.prototype.getExecutors = function (action, category) {
        // If no action or category is given, return empty set of executors
        if (!action || !category) {
            return [];
        }
        // Find the executors based on the given action and scope
        return this.executors.filter(function (executor) {
            return (executor.action === action && executor.category === category);
        });
    };
    /**
     * Get the first executor based on action and scope
     * @param {ExecutorAction} action The executor action
     * @param {ExecutorScope} scope The scope of the executor
     * @returns {string}
     */
    ExecutorsService.prototype.getFirstExecutorName = function (action, scope) {
        // If no action or scope is given, return empty set of executors
        if (!action || !scope) {
            return undefined;
        }
        // Find the FIRST executors based on the given action and scope
        var foundExecutor = this.executors.find(function (executor) {
            return (executor.action === action && executor.scopes.indexOf(scope) !== -1);
        });
        // If executor is found, return the name of it
        return (foundExecutor) ? foundExecutor.name : undefined;
    };
    /**
     * Get an executor by the name (sync)
     * @param {string} name The name of the name
     * @returns {ExecutorBase}
     */
    ExecutorsService.prototype.getExecutorByName = function (name) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Search for executor based on name and stop by the first hit
            var foundExecutor = _this.executors.find(function (executor) {
                return (executor.name === name);
            });
            if (!foundExecutor) {
                reject(new Error('Cannot find the executor named "' + name + '"'));
            }
            // Next step
            resolve(foundExecutor);
        });
    };
    /**
     * Create executor event and returns the generated id
     * @param {ExecutorName} executorName The executor name
     * @param {ExecutorData} data The data related to the event
     * @returns {ExecutorId}
     */
    ExecutorsService.prototype.createExecutorEventAndGetTheId = function (executorName, data) {
        // Generate event id
        var id = this.generateExecutorId(executorName);
        // Create executor event and add it to executor events
        this.events.unshift({
            id: id,
            dragStart: moment(),
            executorName: executorName,
            data: data
        });
        // Clean up the 'older' events
        if (this.events.length > this.maxExecutorEvents) {
            this.events.length = this.maxExecutorEvents;
        }
        // Trigger the show drop targets
        this.getExecutorByName(executorName).then(function (executor) {
            executor.showDropTargets(data);
        });
        // Return the ID
        return id;
    };
    /**
     * Get the executor event by id
     * @param {ExecutorId} id The id of the executor
     * @returns
     */
    ExecutorsService.prototype.getExecutorEventById = function (id) {
        // Find the event
        var found = this.events.find(function (event) {
            return (event.id === id);
        });
        // Clean up the event
        this.events = this.events.filter(function (event) {
            return (event.id !== id);
        });
        return found;
    };
    /**
     * Generates an executor id and make it (more) human readable by adding the executor name
     * to the begin of the id.
     * @param {ExecutorName} executorName The name of the executor
     * @returns {ExecutorId}
     */
    ExecutorsService.prototype.generateExecutorId = function (executorName) {
        // From http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript
        return executorName + '-' + (Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
    };
    return ExecutorsService;
}());
export { ExecutorsService };
